// client/src/pages/secure/SiteSearchClipPage.js — where "Send to BREC" lands.
//
// The bookmarklet posts the listing page (URL, title, text) to the server, which keeps it
// for 30 minutes under a random id and sends the browser here with ?clip=<id>. If the
// listing site blocked the post, the bookmarklet opens this page with only the URL and
// title in the #fragment, and the owner pastes the text. Without either, the page lists
// the pages still waiting. Nothing is saved until the owner checks the form and saves.
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import AddListingPaste from '../../components/secure/AddListingPaste';

const fromHash = () => {
    const h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const url = (h.get('u') || '').slice(0, 2000);
    return url || h.get('h') ? { url, title: (h.get('h') || '').slice(0, 300), text: '' } : null;
};

const SiteSearchClipPage = () => {
    const apiClient = useApiClient();
    const [params, setParams] = useSearchParams();
    const clipId = params.get('clip');
    const [initial, setInitial] = useState(null);
    const [blocked, setBlocked] = useState(false);
    const [waiting, setWaiting] = useState([]);
    const [searches, setSearches] = useState(null);
    const [error, setError] = useState('');
    const [savedTo, setSavedTo] = useState(null);

    useEffect(() => {
        apiClient.get('/api/site-searches').then((rows) => setSearches(rows.filter((s) => s.status !== 'closed')))
            .catch((err) => setError(err.message || 'Could not list the searches.'));
    }, [apiClient]);

    const loadWaiting = useCallback(() => {
        apiClient.get('/api/site-searches/clips').then(setWaiting).catch(() => setWaiting([]));
    }, [apiClient]);

    useEffect(() => {
        setInitial(null); setError(''); setSavedTo(null);
        if (clipId) {
            apiClient.get(`/api/site-searches/clips/${encodeURIComponent(clipId)}`)
                .then((c) => setInitial({ url: c.url || '', title: c.title || '', text: c.text || '', parsed: c.parsed }))
                .catch((err) => { setError(err.message || 'That page is no longer waiting.'); loadWaiting(); });
            return;
        }
        const h = fromHash();
        if (h) { setBlocked(true); setInitial(h); return; }
        setInitial({ url: '', title: '', text: '' });
        loadWaiting();
    }, [apiClient, clipId, loadWaiting]);

    const discard = async (id) => {
        try { await apiClient.delete(`/api/site-searches/clips/${id}`); } catch { /* gone already */ }
        if (id === clipId) setParams({}); else loadWaiting();
    };

    const saved = (r, target) => setSavedTo({ ...r, search: (searches || []).find((s) => String(s.search_id) === String(target)) });

    return (
        <div className="mx-auto max-w-3xl space-y-4 p-4">
            <div>
                <h1 className="text-xl font-bold text-gray-900">Add a listing</h1>
                <p className="text-sm text-gray-600">A listing you sent with <b>Send to BREC</b> or pasted. Check the fields, pick the search, save.</p>
            </div>
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            {blocked && (
                <p className="rounded border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    That site would not let the page text be sent. Go back to its tab, press Ctrl+A then Ctrl+C, and paste into
                    <b> Page text</b> below.
                </p>
            )}
            {savedTo?.search && (
                <p className="rounded bg-green-50 px-3 py-2 text-sm text-green-800">
                    {savedTo.message} <Link className="font-semibold underline" to={`/dashboard/clients/${savedTo.search.client_id}`}>
                        Open {savedTo.search.client_name}'s record</Link> → Site search → {savedTo.search.title}.
                </p>
            )}
            {!clipId && !!waiting.length && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pages waiting (kept 30 minutes)</h2>
                    <ul className="mt-2 divide-y text-sm">
                        {waiting.map((w) => (
                            <li key={w.clip_id} className="flex items-center justify-between gap-2 py-1.5">
                                <span className="truncate">{w.title || w.url || 'Untitled page'}
                                    <span className="ml-1 text-xs text-gray-400">{new Date(w.created_at).toLocaleTimeString()}</span></span>
                                <span className="flex shrink-0 gap-2">
                                    <button onClick={() => setParams({ clip: w.clip_id })} className="rounded bg-slate-800 px-2 py-1 text-xs font-semibold text-white">Open</button>
                                    <button onClick={() => discard(w.clip_id)} className="rounded border border-gray-300 px-2 py-1 text-xs">Discard</button>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {initial && searches && (
                <div className="rounded border border-gray-200 bg-white p-4">
                    {searches.length
                        ? <ClipForm key={clipId || 'manual'} initial={initial} clipId={clipId} searches={searches} onSaved={saved} />
                        : <p className="text-sm text-gray-600">No open site search yet. Start one from the client's record → Site search.</p>}
                </div>
            )}
        </div>
    );
};

// Remembers which search was picked, so the success line can link to it.
const ClipForm = ({ initial, clipId, searches, onSaved }) => {
    const [pick] = useState(() => (searches.length === 1 ? searches[0].search_id : ''));
    return (
        <AddListingPaste searchId={pick} searches={searches} initial={initial} clipId={clipId}
                         onDone={(r) => onSaved(r, r && r.search_id)} />
    );
};

export default SiteSearchClipPage;
